<script setup lang="ts">
import { PencilLine, History, Loader2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ringCentralAPI } from '@/api/ringcentral-api';
import type { StaffNameAmendment } from '@/types/api';
import { useRingCentralStore } from '@/stores/ringcentral';

interface Props {
  telephonySessionId: string;
  orgId: string; // siteId from call data
  currentStaffName: string;
  originalStaffName?: string;
  amendmentHistory?: StaffNameAmendment[];
  iconType?: 'edit' | 'history'; // Icon type: 'edit' for PencilLine, 'history' for History icon
}

const props = defineProps<Props>();
const emit = defineEmits<{
  success: [
    response: {
      telephonySessionId: string;
      currentStaffName: string;
      originalStaffName?: string;
      previousStaffName: string;
      amendedBy: string;
      amendedAt: string;
    },
  ];
}>();

const ringCentralStore = useRingCentralStore();
const open = ref(false);
const newStaffName = ref('');
const reason = ref('');
const isSubmitting = ref(false);
const loadingOrganization = ref(false);
const errorMessage = ref('');

// Get organization from ringcentral store using the call's orgId (siteId)
const organizationDetails = computed(() => {
  if (!props.orgId) {return null;}
  // Check if this organization exists in the cached list with full details
  return ringCentralStore.organizations.find(org => org.organizationId === props.orgId) || null;
});

// Parse staff list from organization businessConfig
// Staff names are newline-separated in DynamoDB
const staffList = computed(() => {
  const staffString = organizationDetails.value?.businessConfig?.staff || '';
  if (!staffString.trim()) {return [];}
  return staffString
    .split('\n')
    .map((name: string) => name.trim())
    .filter((name: string) => name.length > 0);
});

// Check if we should show dropdown or input
const hasStaffList = computed(() => staffList.value.length > 0);

// Reset form and fetch organization details when dialog opens
watch(open, async isOpen => {
  if (isOpen) {
    // Reset form
    // Convert empty string to "__NONE__" for Select component compatibility
    newStaffName.value = props.currentStaffName || '__NONE__';
    reason.value = '';
    errorMessage.value = '';

    // Fetch full organization details (with businessConfig) from RingCentral store
    // Use the call's orgId (siteId) to get the correct staff list
    if (props.orgId) {
      loadingOrganization.value = true;
      try {
        await ringCentralStore.getOrganization(props.orgId);
        console.log('✅ Organization loaded for orgId:', props.orgId);
        console.log('✅ Staff list:', staffList.value);
      } catch (error) {
        console.warn('Failed to fetch organization details:', error);
      } finally {
        loadingOrganization.value = false;
      }
    }
  }
});

async function handleSubmit() {
  errorMessage.value = '';

  // Convert special "__NONE__" value to empty string for backend
  const staffNameToSubmit = newStaffName.value === '__NONE__' ? '' : newStaffName.value.trim();
  const currentNameNormalized = props.currentStaffName || '';

  // Check if the new name is the same as current name
  if (staffNameToSubmit === currentNameNormalized) {
    errorMessage.value = 'The staff name is the same';
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await ringCentralAPI.amendCallStaffName(
      props.telephonySessionId,
      staffNameToSubmit,
      reason.value.trim() || undefined,
    );

    open.value = false;

    // Emit the full response data so parent can update local state including amendment history
    emit('success', {
      telephonySessionId: response.telephonySessionId,
      currentStaffName: response.currentStaffName,
      originalStaffName: response.originalStaffName,
      previousStaffName: response.previousStaffName,
      amendedBy: response.amendedBy,
      amendedAt: response.amendedAt,
    });
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to amend staff name';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <Dialog v-model:open="open">
    <Button
      variant="ghost"
      size="sm"
      @click="open = true"
      class="h-7 w-7 p-0 rounded-md hover:bg-accent hover:shadow-md transition-all duration-200 group"
      :title="iconType === 'history' ? 'Amend staff name (previously amended)' : 'Edit staff name'"
    >
      <History
        v-if="iconType === 'history'"
        class="h-3.5 w-3.5 text-amber-600 dark:text-amber-500 group-hover:text-amber-700 dark:group-hover:text-amber-400 group-hover:scale-110 transition-all duration-200"
      />
      <PencilLine
        v-else
        class="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground group-hover:scale-110 transition-all duration-200"
      />
    </Button>

    <DialogContent class="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Amend Staff Name</DialogTitle>
        <DialogDescription>
          Update the staff member name for this call. Changes are tracked with full audit history.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <!-- Error message -->
        <div v-if="errorMessage" class="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
          {{ errorMessage }}
        </div>

        <!-- Show original name if amended -->
        <div
          v-if="originalStaffName && originalStaffName !== currentStaffName"
          class="rounded-lg bg-muted p-3"
        >
          <div class="flex items-center gap-2 text-sm">
            <History class="h-4 w-4 text-muted-foreground" />
            <span class="text-muted-foreground">Original:</span>
            <Badge variant="outline">{{ originalStaffName }}</Badge>
          </div>
        </div>

        <!-- Staff Name Change (Current + New in one box) -->
        <div class="space-y-3 border rounded-lg p-4 bg-muted/30">
          <!-- Current name -->
          <div class="space-y-2">
            <Label class="text-xs text-muted-foreground">Current Name</Label>
            <div class="text-sm font-medium">{{ currentStaffName || '(Not set)' }}</div>
          </div>

          <!-- Divider -->
          <div class="border-t" />

          <!-- New name selection -->
          <div class="space-y-2">
            <Label for="new-name">New Name *</Label>

            <!-- Loading state -->
            <div
              v-if="loadingOrganization"
              class="flex items-center gap-2 border rounded-md p-3 text-sm text-muted-foreground"
            >
              <Loader2 class="h-4 w-4 animate-spin" />
              <span>Loading staff list...</span>
            </div>

            <!-- Dropdown selection (when staff list exists) -->
            <Select v-else-if="hasStaffList" v-model="newStaffName" :disabled="isSubmitting">
              <SelectTrigger id="new-name" class="w-full">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem v-for="name in staffList" :key="name" :value="name">
                    {{ name }}
                  </SelectItem>
                  <!-- Empty option at the end for "no one answered" -->
                  <SelectItem value="__NONE__"> (No staff / Unanswered) </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <!-- Fallback message when no staff list configured -->
            <div v-else class="text-sm text-muted-foreground border rounded-md p-3">
              No staff list configured for this organization. Please contact your administrator to
              add staff names.
            </div>
          </div>
        </div>

        <!-- Reason (optional) -->
        <div class="space-y-2">
          <Label for="reason">Reason (optional)</Label>
          <Textarea
            id="reason"
            v-model="reason"
            placeholder="e.g., Added full name, Corrected spelling"
            rows="2"
            :disabled="isSubmitting"
          />
        </div>

        <!-- Amendment history -->
        <div v-if="amendmentHistory && amendmentHistory.length > 0" class="space-y-2">
          <Label class="text-muted-foreground">Amendment History</Label>
          <div class="space-y-2 rounded-lg border p-3 max-h-32 overflow-y-auto">
            <div v-for="(amendment, i) in amendmentHistory" :key="i" class="text-xs space-y-1">
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ amendment.from }}</span>
                <span class="text-muted-foreground">→</span>
                <span class="font-medium">{{ amendment.to }}</span>
              </div>
              <div class="text-muted-foreground">
                {{ new Date(amendment.amendedAt).toLocaleString() }}
                <span v-if="amendment.reason" class="ml-1">• {{ amendment.reason }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter class="flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          @click="open = false"
          :disabled="isSubmitting || loadingOrganization"
          class="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          @click="handleSubmit"
          :disabled="isSubmitting || loadingOrganization"
          class="w-full sm:w-auto"
        >
          <Loader2 v-if="isSubmitting" class="h-4 w-4 mr-2 animate-spin" />
          {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
